import {
  ManageAreaData,
  ResponseSummaryGeoAddressData,
  ResponseSummaryGeoAddress,
  ResponseTransferAreaData,
  ResponseTransferArea,
  Shift,
  AvailableCourierList,
  ManageArea,
  CourierData,
  GeoAddress,
  GeoAddressArea,
  DetailPartnerAreaDedicated,
  DetailGeoAddresses,
  DetailShifts,
  UpdatePartnerAreaDedicated,
  GeoAddressTransferArea
} from "@/domain/entities/ManageArea";
import { AxiosResponse } from "axios";
import { PaginationV2 } from "@/domain/entities/Pagination";
import { convertSnakeToCamel } from "@/app/infrastructures/misc/common-library/ConvertSnakeToCamel";
import { ResponsePayloadV2 } from "@/domain/entities/ResponsePayload";

export class ManageAreaMapper {
  public convertListManageAreaFromApi(res: AxiosResponse<any>): ManageAreaData {
    const { data } = res;
    return new ManageAreaData({
      pagination: new PaginationV2({
        page: data.meta.page,
        limit: data.meta.limit,
        totalData: data.meta.total_records
      }),
      list: data.data.length
        ? data.data.map(
            (item: any) =>
              new ManageArea({
                uid: item.uid,
                areaName: item.area_name,
                totalGeoAddress: item.total_geo_address,
                couriers: item.couriers.length
                  ? item.couriers.map(
                      (courier: any) =>
                        new CourierData({
                          courierId: courier.courier_id,
                          courierName: courier.courier_name,
                          courierPhone: courier.courier_phone,
                          startTime: courier.start_time,
                          endTime: courier.end_time
                        })
                    )
                  : [],
                dedicatedShipment: item.dedicated_shipment,
                createdAt: item.created_at,
                updatedAt: item.updated_at,
                createdBy: item.created_by,
                updatedBy: item.updated_by
              })
          )
        : []
    });
  }

  public convertSummaryGeoAddressFromApi(
    res: AxiosResponse<any>
  ): ResponseSummaryGeoAddressData {
    const { data } = res;

    return new ResponseSummaryGeoAddressData({
      data: new ResponseSummaryGeoAddress({
        assigned: data.data.assigned,
        unassigned: data.data.unassigned
      })
    });
  }
  
  public convertAvailableCourierFromApi(
    res: AxiosResponse<any>
  ): AvailableCourierList {
    const { data } = res;
    return new AvailableCourierList({
      pagination: data.pagination,
      list: data.data.map((item: any) => ({
        id: item.courier_id,
        name: item.fullname,
        phoneNumber: item.phone_number,
        email: item.email
      }))
    });
  }

  public convertAvailableGeoAddressFromApi(res: AxiosResponse) {
    const { data: { data } } = res;

    const geoAddressData = Array.isArray(data) ? data : [];

    return geoAddressData.map((ga: any) => {
      const districts = Array.isArray(ga?.district) ? ga.district : [];

      return new GeoAddressArea({
        provinceName: ga.province_name,
        children: districts.map((d: any) => {
          const subDistricts = Array.isArray(d?.sub_district) ? d.sub_district : [];

          return new GeoAddressArea({
            districtName: d.district_name,
            children: subDistricts.map((sd: any) => {
              const villages = Array.isArray(sd?.village) ? sd.village : [];

              return new GeoAddressArea({
                subDistrictName: sd.sub_district_name,
                children: villages.map((v: any) => {
                  return new GeoAddressArea({
                    villageName: v.village_name,
                    geoAddressId: v.geo_address_id,
                    areaId: v.area_id 
                  })
                })
              })
            })
          })
        })
      })
    })
  }

  convertGeoAddressFromApi(res: AxiosResponse<any>): ResponsePayloadV2 {
    const { data } = res;
    return new ResponsePayloadV2({
      pagination: new PaginationV2({
        page: data.meta.page,
        limit: data.meta.per_page,
        totalData: data.meta.total_record
      }),
      data: data?.data?.map((item: any) => convertSnakeToCamel(item)) || []
    });
  }

  public convertDetailPartnerAreaFromApi(
    res: AxiosResponse<any>
  ): DetailPartnerAreaDedicated {
    const { data } = res;
    return new DetailPartnerAreaDedicated({
      uid: data.data.uid,
      areaName: data.data.area_name,
      geoAddressIds: data.data.geo_address_ids.length
        ? data.data.geo_address_ids
        : [],
      geoAddresses: data.data.geo_addresses.length
        ? data.data.geo_addresses.map(
            (item: any) =>
              new DetailGeoAddresses({
                geoAddressId: item.geo_address_id,
                provinceName: item.province_name,
                districtName: item.district_name,
                subDistrictName: item.sub_district_name,
                villageName: item.village_name
              })
          )
        : [],
      shifts: data.data.shifts.length
        ? data.data.shifts.map(
            (item: any) =>
              new DetailShifts({
                courierId: item.courier_id,
                courierName: item.courier_name,
                startTime: item.start_time,
                endTime: item.end_time
              })
          )
        : []
    });
  }

  public convertUpdatePartnerAreaFromApi(
    res: AxiosResponse<any>
  ): UpdatePartnerAreaDedicated {
    const { data } = res;
    return new UpdatePartnerAreaDedicated({
      areaName: data.data.area_name,
      geoAddressIds: data.data.geo_address_ids.length
        ? data.data.geo_address_ids
        : [],
      shifts: data.data.shifts.length
        ? data.data.shifts.map(
            (item: any) =>
              new DetailShifts({
                courierId: item.courier_id,
                courierName: item.courier_name,
                startTime: item.start_time,
                endTime: item.end_time
              })
          )
        : []
    });
  }

  public convertTransferAreaFromApi(res: AxiosResponse<any>): ResponseTransferAreaData {
    const { data: { data } } = res;
    return new ResponseTransferAreaData({
      data: new ResponseTransferArea({
        uid: data.uid,
        areaName: data.area_name,
        geoAddressIds: data.geo_address_ids,
        geoAddresses: data.geo_addresses.length
          ? data.geo_addresses.map(
              (item: any) =>
                new GeoAddressTransferArea({
                  geoAddressId: item.geo_address_id,
                  provinceName: item.province_name,
                  districtName: item.district_name,
                  subDistrictName: item.sub_district_name,
                  villageName: item.village_name
                })
            )
          : [],
        shifts: data.shifts.map((shift: Shift) => new Shift(shift))
      })
    });
  }
}

